// src/components/ui/AvatarPicker/AvatarPickerModal.tsx
import React, { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { cameraOutline, closeOutline } from 'ionicons/icons';
import { Modal, ModalSection } from '../Modal';
import { AvatarPicker, type AvatarValue } from './AvatarPicker';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface AvatarPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Save handler - called with the new avatar value */
  onSave: (avatar: AvatarValue) => void | Promise<void>;
  /** Current avatar value */
  currentAvatar?: AvatarValue | null;
  /** Title for the modal */
  title?: string;
  /** Subtitle/description */
  subtitle?: string;
  /** User ID for upload path */
  userId?: string;
  /** Google avatar URL (if available) */
  googleAvatarUrl?: string | null;
  /** Storage bucket name */
  storageBucket?: string;
  /** Whether save is in progress */
  saving?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// AVATAR PICKER MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const AvatarPickerModal: React.FC<AvatarPickerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentAvatar,
  title = 'Change Avatar',
  subtitle,
  userId,
  googleAvatarUrl,
  storageBucket = 'avatars',
  saving = false,
}) => {
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarValue | null>(currentAvatar || null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset selected avatar when modal opens with new current value
  useEffect(() => {
    if (isOpen) {
      setSelectedAvatar(currentAvatar || null);
    }
  }, [isOpen, currentAvatar]);

  // Handle save
  const handleSave = async () => {
    if (!selectedAvatar) return;

    setIsSaving(true);
    try {
      await onSave(selectedAvatar);
      onClose();
    } catch (err) {
      console.error('Failed to save avatar:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Check if selection has changed
  const hasChanged = JSON.stringify(selectedAvatar) !== JSON.stringify(currentAvatar);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={{
        title,
        subtitle,
      }}
      footer={{
        primaryAction: {
          label: isSaving || saving ? 'Saving...' : 'Save',
          onClick: handleSave,
          disabled: !selectedAvatar || !hasChanged || isSaving || saving,
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: onClose,
        },
      }}
    >
      <ModalSection spacing="normal">
        {/* Current Avatar Preview */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 24,
          borderBottom: '1px solid var(--ion-border-color)',
        }}>
          <div style={{
            position: 'relative',
            width: 100,
            height: 100,
            marginBottom: 12,
          }}>
            {/* Avatar display */}
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: 'var(--kff-foundry-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              border: '3px solid var(--kff-forge-gold)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}>
              {selectedAvatar?.type === 'upload' || selectedAvatar?.type === 'google' ? (
                <img
                  src={selectedAvatar.value}
                  alt="Selected avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : selectedAvatar?.type === 'icon' ? (
                <span style={{ fontSize: 48 }}>{selectedAvatar.value}</span>
              ) : (
                <IonIcon
                  icon={cameraOutline}
                  style={{ fontSize: 36, color: 'rgba(255,255,255,0.7)' }}
                />
              )}
            </div>
          </div>

          <div style={{
            fontFamily: 'var(--kff-data-font)',
            fontSize: 14,
            color: 'var(--ion-color-medium)',
            textAlign: 'center',
          }}>
            {selectedAvatar ? (
              hasChanged ? 'New avatar selected' : 'Current avatar'
            ) : (
              'Select an avatar below'
            )}
          </div>
        </div>

        {/* Avatar Picker */}
        <AvatarPicker
          value={selectedAvatar}
          onChange={setSelectedAvatar}
          userId={userId}
          googleAvatarUrl={googleAvatarUrl}
          storageBucket={storageBucket}
          defaultTab="icon"
          maxFileSizeMB={2}
        />
      </ModalSection>
    </Modal>
  );
};

AvatarPickerModal.displayName = 'AvatarPickerModal';

// ═══════════════════════════════════════════════════════════════════
// CLICKABLE AVATAR COMPONENT
// ═══════════════════════════════════════════════════════════════════

export interface ClickableAvatarProps {
  /** Current avatar value */
  avatar?: AvatarValue | null;
  /** Avatar URL (for uploaded/google avatars) */
  avatarUrl?: string | null;
  /** Fallback emoji/icon */
  fallback?: string;
  /** Size of the avatar */
  size?: number;
  /** Background color */
  backgroundColor?: string;
  /** Click handler */
  onClick?: () => void;
  /** Whether to show edit overlay on hover */
  showEditOverlay?: boolean;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

export const ClickableAvatar: React.FC<ClickableAvatarProps> = ({
  avatar,
  avatarUrl,
  fallback = '👤',
  size = 64,
  backgroundColor = 'var(--kff-foundry-blue)',
  onClick,
  showEditOverlay = true,
  className,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Determine what to display
  const displayUrl = avatarUrl || (avatar?.type === 'upload' || avatar?.type === 'google' ? avatar.value : null);
  const displayIcon = avatar?.type === 'icon' ? avatar.value : (!displayUrl ? fallback : null);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={className}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        border: 'none',
        padding: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        transform: isHovered && onClick ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isHovered && onClick
          ? '0 4px 12px rgba(0, 0, 0, 0.2)'
          : '0 2px 6px rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        ...style,
      }}
      aria-label={onClick ? 'Change avatar' : 'Avatar'}
    >
      {/* Avatar content */}
      {displayUrl ? (
        <img
          src={displayUrl}
          alt="Avatar"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.15s ease',
            opacity: isHovered && showEditOverlay && onClick ? 0.7 : 1,
          }}
        />
      ) : (
        <span style={{
          fontSize: size * 0.5,
          color: displayIcon === fallback ? 'white' : 'inherit',
          transition: 'opacity 0.15s ease',
          opacity: isHovered && showEditOverlay && onClick ? 0.5 : 1,
        }}>
          {displayIcon}
        </span>
      )}

      {/* Edit overlay */}
      {showEditOverlay && onClick && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.15s ease',
          borderRadius: '50%',
        }}>
          <IonIcon
            icon={cameraOutline}
            style={{
              fontSize: size * 0.35,
              color: 'white',
            }}
          />
        </div>
      )}
    </button>
  );
};

ClickableAvatar.displayName = 'ClickableAvatar';

export default AvatarPickerModal;
